import type { ReactNode } from "react"

import {
  OperatorWorkspaceLayout,
  type OperatorWorkspaceLink,
} from "@/features/operator/shared/operator-shell"

type FleetB2BSectionKey =
  | "overview"
  | "onboarding"
  | "operating-units"
  | "assets"
  | "people"
  | "assignments"
  | "kits"
  | "tags"
  | "analytics"
  | "certifications"

const fleetB2BWorkspaceLinks: ReadonlyArray<
  OperatorWorkspaceLink & { key: FleetB2BSectionKey }
> = [
  {
    description: "Operational overview, readiness, and recent fleet activity.",
    key: "overview",
    label: "Overview",
    to: "/fleet",
  },
  {
    description: "Start enterprise onboarding and choose workbook or manual entry.",
    key: "onboarding",
    label: "Onboarding",
    to: "/fleet/onboarding/organization",
  },
  {
    description: "Manage branches, depots, hubs, terminals, and route bases.",
    key: "operating-units",
    label: "Operating Units",
    to: "/fleet/operating-units",
  },
  {
    description: "Review trucks, buses, and two-wheelers as first-class assets.",
    key: "assets",
    label: "Assets",
    to: "/fleet/assets",
  },
  {
    description: "Maintain drivers, helpers, conductors, riders, and supervisors.",
    key: "people",
    label: "People",
    to: "/fleet/people",
  },
  {
    description: "Track active and historical assignments between assets and people.",
    key: "assignments",
    label: "Assignments",
    to: "/fleet/assignments",
  },
  {
    description: "Issue one activated kit per asset and prepare print batches.",
    key: "kits",
    label: "Kits",
    to: "/fleet/kits",
  },
  {
    description: "Audit issued sticker codes, scan URLs, and legacy bridge issuance.",
    key: "tags",
    label: "QR Registry",
    to: "/fleet/tags",
  },
  {
    description: "Monitor enterprise safety outcomes across routes, hubs, and assets.",
    key: "analytics",
    label: "Analytics",
    to: "/fleet/analytics",
  },
  {
    description: "Review certification status and supporting compliance artifacts.",
    key: "certifications",
    label: "Certifications",
    to: "/fleet/certifications",
  },
]

interface FleetB2BPageShellProps {
  activeSection: FleetB2BSectionKey
  children?: ReactNode
  description?: string
  highlights: readonly string[]
  title: string
}

export function FleetB2BPageShell(props: FleetB2BPageShellProps) {
  const { children, description, title } = props

  return (
    <OperatorWorkspaceLayout
      hideSummarySection
      links={fleetB2BWorkspaceLinks.map((link) => ({ ...link }))}
    >
      <section className="grid gap-5">
        <div className="grid gap-1 px-1">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {description ? (
            <p className="text-sm leading-6 text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {children}
      </section>
    </OperatorWorkspaceLayout>
  )
}
